import { Directive, DoCheck, ElementRef, OnDestroy, Optional, Renderer2, Self } from '@angular/core';
import { NgControl, ValidationErrors } from '@angular/forms';
import { ValidacaoObrigatoriosDirective } from './validacao-obrigatorios.directive';

/**
 * Mensagem exibida para cada erro de validação, na ordem de prioridade em que
 * devem ser testados: "obrigatório" sempre vem antes dos erros de formato.
 */
export const MENSAGENS_POR_ERRO: ReadonlyArray<[string, string]> = [
    ['required', 'Preenchimento obrigatório'],
    ['cpfNotValid', 'CPF inválido'],
    ['cnpjNotValid', 'CNPJ inválido'],
    ['cpfOrCnpjNotValid', 'Informe um CPF ou CNPJ válido'],
    ['telefoneInvalido', 'Informe um telefone válido'],
    ['invalidEmail', 'Informe um e-mail válido'],
    ['email', 'Informe um e-mail válido'],
    ['pattern', 'Preencha o campo no formato esperado'],
    ['minlength', 'Preencha o campo no formato esperado'],
    ['maxlength', 'Preencha o campo no formato esperado'],
];

export const MENSAGEM_PADRAO = 'Preenchimento inválido';

/**
 * Exibe, logo abaixo do campo, a mensagem que explica por que ele está inválido
 * (por padrão "Preenchimento obrigatório") e remove a mensagem assim que um
 * valor válido é informado.
 *
 * Casa com qualquer controle reativo, independentemente do tipo (input, select,
 * textarea, p-autoComplete, checkbox), mas só age dentro de um formulário
 * marcado com `ValidacaoObrigatorios`. Fora desse escopo não altera nada.
 *
 * Além da mensagem visual, marca o campo com `aria-invalid` e o associa ao
 * texto do erro via `aria-describedby`, para leitores de tela.
 */
@Directive({
    selector: '[formControlName]',
})
export class FieldErrorDirective implements DoCheck, OnDestroy {

    private static readonly MESSAGE_CLASS = 'field-error-message';
    private static sequencial = 0;

    private messageElement: HTMLElement;
    private messageId: string;
    private currentMessage: string = null;
    private describedByAplicado = false;

    constructor(
        private el: ElementRef<HTMLElement>,
        private renderer: Renderer2,
        @Optional() @Self() private ngControl: NgControl,
        @Optional() private escopo: ValidacaoObrigatoriosDirective,
    ) { }

    ngDoCheck(): void {
        const message = this.resolveMessage();

        if (message === this.currentMessage) {
            return;
        }

        this.currentMessage = message;

        if (message) {
            this.renderMessage(message);
        } else {
            this.removeMessage();
        }
    }

    ngOnDestroy(): void {
        this.removeMessage();
    }

    /**
     * Mensagem que deve ser exibida, ou null quando o campo não deve ser
     * sinalizado (fora do escopo, válido ou ainda não tocado).
     */
    private resolveMessage(): string {
        if (!this.escopo) {
            return null;
        }

        const control = this.ngControl ? this.ngControl.control : null;

        if (!control || control.disabled || control.valid || !control.touched) {
            return null;
        }

        return this.mensagemPara(control.errors);
    }

    private mensagemPara(errors: ValidationErrors | null): string {
        if (!errors) {
            return MENSAGEM_PADRAO;
        }

        for (const [chave, mensagem] of MENSAGENS_POR_ERRO) {
            if (errors[chave]) {
                return mensagem;
            }
        }

        return MENSAGEM_PADRAO;
    }

    private renderMessage(message: string): void {
        if (!this.messageElement) {
            this.messageId = 'campo-erro-' + (++FieldErrorDirective.sequencial);

            this.messageElement = this.renderer.createElement('small');
            this.renderer.addClass(this.messageElement, FieldErrorDirective.MESSAGE_CLASS);
            this.renderer.setAttribute(this.messageElement, 'id', this.messageId);
            this.renderer.setAttribute(this.messageElement, 'role', 'alert');

            const ancora = this.resolveAnchor();
            this.renderer.insertBefore(ancora.parent, this.messageElement, ancora.before);

            this.aplicarAcessibilidade();
        }

        this.renderer.setProperty(this.messageElement, 'textContent', message);
    }

    private removeMessage(): void {
        if (!this.messageElement) {
            return;
        }

        this.removerAcessibilidade();

        const parent = this.renderer.parentNode(this.messageElement);

        if (parent) {
            this.renderer.removeChild(parent, this.messageElement);
        }

        this.messageElement = null;
        this.messageId = null;
    }

    private aplicarAcessibilidade(): void {
        const host = this.el.nativeElement;

        this.renderer.setAttribute(host, 'aria-invalid', 'true');

        // Não sobrescreve um aria-describedby que o template já tenha definido
        if (!host.getAttribute('aria-describedby')) {
            this.renderer.setAttribute(host, 'aria-describedby', this.messageId);
            this.describedByAplicado = true;
        }
    }

    private removerAcessibilidade(): void {
        const host = this.el.nativeElement;

        this.renderer.removeAttribute(host, 'aria-invalid');

        if (this.describedByAplicado) {
            this.renderer.removeAttribute(host, 'aria-describedby');
            this.describedByAplicado = false;
        }
    }

    /**
     * A mensagem fica imediatamente depois do próprio campo, para não se
     * desassociar dele quando a célula tiver mais de um controle. Em
     * checkbox/radio do Bootstrap o label é irmão do input, então a mensagem
     * vai para o fim do bloco, depois do texto.
     */
    private resolveAnchor(): { parent: HTMLElement; before: Node | null } {
        const host = this.el.nativeElement;
        const customControl = host.closest ? host.closest('.custom-control') : null;

        if (customControl) {
            return { parent: customControl as HTMLElement, before: null };
        }

        const parent = host.parentElement;

        if (!parent) {
            return { parent: host, before: null };
        }

        return { parent, before: host.nextSibling };
    }
}
